import type { ColumnDef } from '@tanstack/react-table';
import { Check } from 'lucide-react';

import type { SignalAlertPreviewIssue } from '../model';
import { CONDITION_OPERATOR } from '../model';

// 트리거 조건 타입
export type TriggerConditions = {
  feedbackCountCondition: { enabled: boolean; threshold: number };
  changeRateCondition: { enabled: boolean; threshold: number };
  conditionOperator: (typeof CONDITION_OPERATOR)[keyof typeof CONDITION_OPERATOR];
};

// row가 발송 조건을 만족하는지 판단
function isTriggered(
  issue: SignalAlertPreviewIssue,
  conditions: TriggerConditions,
): boolean {
  const { feedbackCountCondition, changeRateCondition, conditionOperator } =
    conditions;

  const feedbackPassed =
    feedbackCountCondition.enabled &&
    issue.feedbackCount >= feedbackCountCondition.threshold;
  const changeRatePassed =
    changeRateCondition.enabled &&
    issue.changeRate !== null &&
    issue.changeRate >= changeRateCondition.threshold;

  // 둘 다 비활성화면 triggered 아님
  if (!feedbackCountCondition.enabled && !changeRateCondition.enabled) {
    return false;
  }

  // 하나만 활성화된 경우
  if (!feedbackCountCondition.enabled) return changeRatePassed;
  if (!changeRateCondition.enabled) return feedbackPassed;

  // 둘 다 활성화된 경우
  if (conditionOperator === CONDITION_OPERATOR.AND) {
    return feedbackPassed && changeRatePassed;
  }
  return feedbackPassed || changeRatePassed;
}

export const useSignalAlertPreviewTableColumn = () => {
  const createColumns = (
    duration: number,
    conditions: TriggerConditions,
  ): ColumnDef<SignalAlertPreviewIssue, any>[] => [
    {
      accessorKey: 'name',
      header: 'Issue',
      meta: { className: 'flex-1' },
    },
    {
      accessorKey: 'feedbackCount',
      header: 'Post',
      size: 120,
      cell: ({ getValue }) => {
        const count = getValue<number>();
        return <span>{count.toLocaleString()}</span>;
      },
    },
    {
      accessorKey: 'changeRate',
      header: `${duration} days changes`,
      size: 140,
      cell: ({ getValue }) => {
        const rate = getValue<number | null>();
        if (rate === null) {
          return <span className="text-muted-foreground font-medium">New</span>;
        }
        const isPositive = rate > 0;
        const isZero = rate === 0;
        return (
          <span
            className={
              isZero
                ? 'text-muted-foreground'
                : isPositive
                  ? 'text-destructive font-medium'
                  : 'text-green-600 font-medium'
            }
          >
            {isPositive ? '+' : ''}
            {rate}%
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      size: 120,
      cell: ({ row }) => {
        const triggered = isTriggered(row.original, conditions);
        if (triggered) {
          return (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Check className="size-4" />
              Triggered
            </span>
          );
        }
        return <span className="text-muted-foreground">-</span>;
      },
    },
  ];

  return { createColumns };
};
